"use client";

import * as React from "react";
import * as yup from "yup";
import { resetPassword } from "#src/utils/aws-amplify-auth";
import Alert, { AlertProps } from "#src/app/(app)/_components/alert/Alert";
import Input from "#src/app/(app)/_components/input/Input";
import Button from "#src/app/(app)/_components/button/Button";
import Link from "next/link";
import { ArrowLeftIcon } from "@heroicons/react/solid";
import { useAuthState } from "#src/app/(auth)/AuthContext";

type RequestNewPasswordFormValues = yup.InferType<
  typeof requestNewPasswordSchema
>;

const requestNewPasswordSchema = yup.object({
  email: yup
    .string()
    .nullable()
    .email("The email is invalid")
    .required("An email is required"),
});

export default function RequestPassword() {
  const { authState, setAuthState } = useAuthState();

  const [errors, setErrors] = React.useState<any>({});
  const [loading, setLoading] = React.useState(false);
  const [alert, setAlert] = React.useState<AlertProps>({
    status: Alert.Status.PRIMARY,
    text: "Please enter the email address you used to sign up and we will send you a link to reset your password",
  });

  const sendNewPasswordRequest = async ({
    email,
  }: RequestNewPasswordFormValues) => {
    try {
      await requestNewPasswordSchema.validate({ email });

      const output = await resetPassword({
        username: email,
      });

      setAlert({
        status: Alert.Status.PRIMARY,
        text: "Please check your email and follow the instructions in the message we have just sent to you. If you don't receive the email, please give our dealers a call.",
      });

      setAuthState(output);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors({
          email: { message: error.message },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);

    const formData = new FormData(e.target as HTMLFormElement);

    const { email } = Object.fromEntries(formData.entries());
    await sendNewPasswordRequest({ email: email.toString() });
  };

  if (authState) {
    return (
      <div className="flex flex-col justify-center space-y-6">
        <Alert status={alert.status} text={alert.text} />
      </div>
    );
  }

  return (
    <form
      className="flex flex-col justify-center space-y-6"
      onSubmit={handleSubmit}
    >
      <h1 className="block w-full text-2xl text-center text-gray-800">
        Reset your password
      </h1>

      {alert && (
        <div className="mb-6" data-testid="page-alert">
          <Alert status={alert.status} text={alert.text} icon={alert.icon} />
        </div>
      )}

      <Input
        type="text"
        name="email"
        label="Email address"
        placeholder="Enter your email"
        errors={errors}
      />

      <div className="flex justify-between">
        <Link href="/login" className="flex items-center gap-2">
          <ArrowLeftIcon width="16" />
          Back
        </Link>

        <Button loading={loading} size={Button.Size.LARGE} disabled={loading}>
          Send Code
        </Button>
      </div>
    </form>
  );
}
